import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { ref, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { Sidebar } from "@eudonet/opal";
import EudonetLogo from "@/shared/components/EudonetLogo.vue";
import { configService } from "@/infrastructure/services/ConfigService";
import { useGlobalSideMenu } from "../../stores/useGlobalSideMenu";
import { filterNavigation } from "../../utils/menuVisibility";
import { filterByApplication } from "../../utils/applicationFilter";
// Import Remix Icon SVG files
import homeLine from "remixicon/icons/Buildings/home-line.svg?url";
import sparkling2Line from "remixicon/icons/Weather/sparkling-2-line.svg?url";
import buildingLine from "remixicon/icons/Buildings/building-line.svg?url";
import megaphoneLine from "remixicon/icons/Business/megaphone-line.svg?url";
import walletLine from "remixicon/icons/Finance/wallet-line.svg?url";
import handHeartLine from "remixicon/icons/Finance/hand-heart-line.svg?url";
import folderChartLine from "remixicon/icons/Document/folder-chart-line.svg?url";
import fileTextLine from "remixicon/icons/Document/file-text-line.svg?url";
import calendar2Line from "remixicon/icons/Business/calendar-2-line.svg?url";
import contactsLine from "remixicon/icons/User & Faces/contacts-line.svg?url";
import groupLine from "remixicon/icons/User & Faces/group-line.svg?url";
import teamLine from "remixicon/icons/User & Faces/team-line.svg?url";
import folderUserLine from "remixicon/icons/Document/folder-user-line.svg?url";
// Action sociale (CD93) entries — glyphs read from the Figma sidebar frame.
// calendar2Line is not re-imported here: the scheduling entry above already
// pulls the same glyph, and a second import breaks the build.
import contactsBookLine from "remixicon/icons/Document/contacts-book-line.svg?url";
import folderOpenLine from "remixicon/icons/Document/folder-open-line.svg?url";
import pieChartLine from "remixicon/icons/Business/pie-chart-line.svg?url";
import group2Line from "remixicon/icons/User & Faces/group-2-line.svg?url";
import checkboxLine from "remixicon/icons/System/checkbox-line.svg?url";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useAnalyticsTracker } from "@/shared/composables/analytics/useAnalyticsTracker";
import { useApplicationAccess } from "@/shared/composables/useApplicationAccess";
export default /*@__PURE__*/ _defineComponent({
    __name: 'MainSidebar',
    setup(__props) {
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const { track } = useAnalyticsTracker();
        const { can } = usePermissions();
        const { accessibleCodes } = useApplicationAccess();
        const globalSideMenu = useGlobalSideMenu();
        // Sidebar configuration
        const companyName = ref("Eudonet");
        const isSidebarOpen = ref(true);
        // Theme configuration - can be made dynamic or from user settings
        const sidebarTheme = ref("dark"); // 'light' | 'dark' | 'auto'
        // Static navigation items with translation keys.
        // Only entries backed by a real route are kept — unwired placeholder
        // sections (Bookmarks, Contribution, Solicitation, Training, Reporting,
        // Event, Agenda, Community portal) and Marketing's unrouted children were
        // removed. "Home" routes to the application root (home module).
        // The AI assistant entry is dropped when the orbitalEnabled runtime flag is
        // off (OP-6663: release, production) — the /assistant route itself is not
        // registered either (main.ts).
        //
        // `key` is the stable identifier used by the temporary per-environment
        // allow-list. Section keys ("marketing", "finance", "collecte") are NOT
        // listable — a section follows its children. Never rename a key without
        // updating every Helm overlay that names it.
        //
        // `applicationCode` ties an entry to the OPUS application it belongs to; the
        // entry is shown only when that application is in the user's accessible set
        // (empty set = no filtering, see filterByApplication). Untagged entries are
        // cross-cutting and always visible; sections stay untagged and follow their
        // children.
        const navigationConfig = [
            {
                key: "home",
                labelKey: "sidebar.home",
                icon: homeLine,
                path: "/",
            },
            {
                key: "assistant",
                labelKey: "sidebar.assistantIa",
                icon: sparkling2Line,
                path: "/assistant",
            },
            {
                key: "contacts",
                labelKey: "sidebar.contact",
                icon: contactsLine,
                path: "/contacts",
                applicationCode: "ophub",
            },
            {
                key: "organisations",
                labelKey: "sidebar.company",
                icon: buildingLine,
                path: "/organisations",
                applicationCode: "ophub",
            },
            {
                key: "marketing",
                labelKey: "sidebar.marketing",
                icon: megaphoneLine,
                children: [
                    {
                        key: "marketing.campaigns",
                        labelKey: "sidebar.multiCanalCampaigns",
                        path: "/communication",
                        applicationCode: "opink",
                    },
                    {
                        key: "marketing.templates",
                        labelKey: "sidebar.templates",
                        path: "/templating/templates",
                        applicationCode: "opink",
                    },
                ],
            },
            {
                key: "finance",
                labelKey: "sidebar.finance",
                icon: walletLine,
                children: [
                    {
                        key: "finance.donations",
                        labelKey: "finance.donations.title",
                        path: "/donations",
                        applicationCode: "opdon",
                    },
                    {
                        key: "finance.subscriptions",
                        labelKey: "finance.subscriptions.title",
                        path: "/subscriptions",
                        applicationCode: "opdon",
                    },
                ],
            },
            {
                key: "collecte",
                labelKey: "collecte.title",
                icon: handHeartLine,
                children: [
                    {
                        key: "collecte.forms",
                        labelKey: "collecte.forms.title",
                        path: "/collecte/forms",
                        applicationCode: "opdonationform",
                    },
                ],
            },
            {
                key: "projects",
                labelKey: "projects.title",
                icon: folderChartLine,
                path: "/projects",
                // Deliberately NOT tagged with applicationCode "oproj": the OPUS application catalog has
                // not been confirmed to emit that code, and a code it never emits would hide the entry
                // for everyone once application filtering activates (see the scheduling entry above).
                // Tag it once "oproj" is confirmed in the catalog.
            },
            {
                key: "documents",
                labelKey: "sidebar.documents",
                icon: fileTextLine,
                path: "/documents",
                applicationCode: "opdoc",
            },
            // ── Action sociale (CD93) ────────────────────────────────────────────────
            // Entries, labels, order and glyphs come from the "Accueil — Action sociale"
            // Figma sidebar frame (node 20375-6702). The dashboard (/social-home, OP-6944)
            // and the appointments list are wired. The five others are deliberate
            // NO-OPs — `path: "#"` is the convention `handleItemClick` already ignores,
            // so they render exactly as designed and do nothing when clicked, pending
            // their own stories.
            //
            // They are shown ONLY where an overlay allow-lists them (OP-6808
            // `ui.menuVisibleKeys`) — see k8s/gitops/overlays/develop/values.yaml on the
            // CD93 branch. `filterNavigation` can only hide, never reveal, so the
            // allow-list is what selects the Action sociale menu on that environment.
            {
                key: "social-dashboard",
                labelKey: "sidebar.socialDashboard",
                icon: homeLine,
                path: "/social-home",
            },
            {
                key: "social-users",
                labelKey: "sidebar.socialUsers",
                icon: contactsBookLine,
                path: "#",
            },
            {
                key: "social-files",
                labelKey: "sidebar.socialFiles",
                icon: folderOpenLine,
                path: "#",
            },
            {
                // Same target as schedulingEntry, and gated on the same capability in
                // sidebarItems: the route guard 403s without it.
                key: "social-appointments",
                labelKey: "sidebar.socialAppointments",
                icon: calendar2Line,
                path: "/scheduling/appointments",
            },
            {
                // Reuses the existing `sidebar.reporting` key: its French value is already
                // exactly the label the Figma frame writes ("Rapports").
                key: "social-reports",
                labelKey: "sidebar.reporting",
                icon: pieChartLine,
                path: "#",
            },
            {
                key: "social-team",
                labelKey: "sidebar.socialTeam",
                icon: group2Line,
                path: "#",
            },
            {
                key: "social-collective-actions",
                labelKey: "sidebar.socialCollectiveActions",
                icon: checkboxLine,
                path: "#",
            },
        ].filter((item) => configService.isOrbitalEnabled || item.path !== "/assistant");
        // Scheduling entry — gated on the same capability as the routes behind it; SCHEDULING_META in
        // modules/scheduling/router owns the why. Hidden, not disabled: the guard 403s on click.
        const schedulingEntry = {
            key: "scheduling",
            labelKey: "scheduling.pageTitle",
            icon: calendar2Line,
            // Lands on the Appointments tab (SchedulingTabs' default) rather than bare /scheduling,
            // which renders the Timeslots tab — the appointments list is what users expect to see first.
            path: "/scheduling/appointments",
            // "opsol" is the OPUS application code, not the capability. "opagd" exists nowhere as an OPUS
            // application, so that tag would hide the entry for everyone once filtering activates. Retag to
            // "opagd" once eudonet-opagd is in the OPUS catalog and emits that code.
            applicationCode: "opsol",
        };
        // CONSTRAINT: gated on the same capability as its route's `requiresCapability` (`opsol.read`,
        // reception/router). Diverge and the entry either hides from a persona the page admits, or leads
        // one to a route the guard then refuses.
        const usagersEntry = {
            key: "reception-usagers",
            labelKey: "reception.residentList.title",
            icon: groupLine,
            path: "/reception/usagers",
            applicationCode: "opsol",
        };
        // Same capability constraint as `usagersEntry`. CONSTRAINT: an environment whose `menuVisibleKeys` allow-list
        // (OP-6808) does not name this key hides the entry; renaming it means updating every overlay that does.
        const socialCaseFilesEntry = {
            key: "reception-social-case-files",
            labelKey: "sidebar.socialFiles",
            icon: folderUserLine,
            path: "/reception/dossiers-sociaux",
            applicationCode: "opsol",
        };
        // Team entry — gated on the same capability as its route; TEAM_META in modules/team/router owns
        // the why. Tagged "opsol" for the same reason as the scheduling entry above.
        const teamEntry = {
            key: "team",
            labelKey: "sidebar.team",
            icon: teamLine,
            path: "/team",
            applicationCode: "opsol",
        };
        // Transform navigation config to sidebar items with translated labels
        // Active state is derived from the current route — never hardcoded
        //
        // Matching uses "path or sub-path of path" so deep routes (e.g.
        // `/documents/{id}/edit`) keep the parent nav item highlighted. The trailing
        // "/" guard avoids `/foo` matching `/foobar`.
        function isPathActive(currentPath, itemPath) {
            return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
        }
        const sidebarItems = computed(() => {
            // Order matters — three stages, each only narrowing the previous one:
            // 1. capability and runtime-flag gates decide what MAY be shown;
            // 2. the application filter keeps entries whose application is accessible
            //    (no-op while the accessible set is empty — fail-open);
            // 3. the temporary per-environment allow-list narrows last, so its no-match
            //    fallback returns the already-access-filtered set and can never reveal
            //    an entry the gates or the application filter denied.
            // Within the social action entries, the order is AS-LD-US-001's: Usagers · Dossiers sociaux · Rendez-vous ·
            // Équipe.
            // CONSTRAINT (CD93 branch): each gated entry is placed right after its Action sociale placeholder, never
            // appended. The allow-list keeps this order and the CD93 one shows the real entry instead of the
            // placeholder, so appending would move Usagers, Dossiers sociaux and Équipe below Actions collectives.
            const canSchedule = can("opsol.scheduling");
            const placedAfter = new Map();
            const placeAfter = (anchorKey, ...entries) => placedAfter.set(anchorKey, [...(placedAfter.get(anchorKey) ?? []), ...entries]);
            if (can("opsol.read"))
                placeAfter("social-users", usagersEntry, socialCaseFilesEntry);
            if (canSchedule)
                placeAfter("social-appointments", schedulingEntry);
            if (can("opsol.absences"))
                placeAfter("social-team", teamEntry);
            const gated = (canSchedule ? navigationConfig : navigationConfig.filter((item) => item.key !== "social-appointments")).flatMap((item) => [item, ...(placedAfter.get(item.key) ?? [])]);
            const appFiltered = filterByApplication(gated, accessibleCodes.value);
            return filterNavigation(appFiltered, configService.menuVisibleKeys).map((item) => {
                const children = item.children?.map((child) => ({
                    label: t(child.labelKey),
                    active: !!child.path && isPathActive(route.path, child.path),
                    path: child.path,
                    testId: `sidebar-item-${child.key}`,
                }));
                // A parent with children is NEVER marked active — the Sidebar handles the
                // "opened" visual state via isSectionExpanded() automatically.
                // Only leaf items (no children) receive the active flag.
                const isActive = !children?.length && !!item.path && isPathActive(route.path, item.path);
                return {
                    label: t(item.labelKey),
                    icon: item.icon,
                    badge: item.badge,
                    active: isActive,
                    path: item.path,
                    children,
                    testId: `sidebar-item-${item.key}`,
                };
            });
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const handleItemClick = (item) => {
            // Leaf items carry their own path and navigate. Parent sections have children
            // and no path: the Sidebar handles them itself — accordion toggle when
            // expanded, reopen-and-expand when collapsed — so no navigation here.
            if (item.path && item.path !== "#") {
                if (item.path === socialCaseFilesEntry.path)
                    track("social_case_menu_clicked");
                router.push(item.path);
            }
            // Close sidebar on mobile
            if (window.innerWidth < 1024) {
                isSidebarOpen.value = false;
            }
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Sidebar), {
                modelValue: isSidebarOpen.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((isSidebarOpen).value = $event)),
                "data-testid": "main-sidebar",
                "company-name": companyName.value,
                items: sidebarItems.value,
                theme: sidebarTheme.value,
                collapsed: _unref(globalSideMenu).collapsed,
                "onUpdate:collapsed": _unref(globalSideMenu).setCollapsed,
                onItemClick: handleItemClick
            }, {
                logo: _withCtx(() => [
                    _createVNode(EudonetLogo, {
                        "logo-only": true,
                        class: _normalizeClass(["h-7 w-7", { 'sidebar-logo-dark': sidebarTheme.value === 'dark' }])
                    }, null, 8 /* PROPS */, ["class"])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "company-name", "items", "theme", "collapsed", "onUpdate:collapsed"]));
        };
    }
});
